import { useState } from "react";
import { Sidebar, Tooltip } from "flowbite-react";
import {
  HiCalendar,
  HiChartPie,
  HiMail,
  HiMenuAlt2,
  HiUserGroup,
} from "react-icons/hi";
import {
  MdAssessment,
  MdPeople,
  MdOutlineTask,
  MdOutlineTaskAlt,
  MdPayment,
  MdSettings,
  MdNaturePeople,
  MdAttachMoney,
  MdAccountBalanceWallet,
} from "react-icons/md";
import { BsBrowserChrome } from "react-icons/bs";
import { FaWpforms } from "react-icons/fa";
import { GrTask, GrProjects, GrUserSettings } from "react-icons/gr";
import { GiMoneyStack } from "react-icons/gi";
import { TbReport } from "react-icons/tb";

import { Link, useLocation } from "react-router-dom";
import { useAllPermissions } from "../utils/usePermission";
import { useLogout } from "../utils/useLogout";
import SessionExpiredModal from "./SessionExpiredModal";

const ACTIVE_CLASS = "actives";

const routeGroups = {
  appointments: ["/appointments"],
  message: ["/messages"],
  tasks: ["/tasks"],
  clients: ["/clients", "/client"],
  staff: ["/staff"],
  appraisal: ["/appraisal"],
  payslip: ["/payslip"],
  capex: ["/capex"],
  opex: ["/opex"],
  vouchers: ["/vouchers"],
  projects: ["/properties", "/property"],
  marketers: ["/marketers"],
  commissions: ["/commissions", "/commission"],
  cashflow: ["/cashflow"],
  newReceipt: ["/receipts"],
  viewReceipt: ["/view-receipts"],
  siteSettings: ["/site-settings"],
  budget: ["/budget-settings"],
};

export default function SideBar({
  isSidebarOpen,
  isCollapsed = false,
  closeSidebar,
  toggleCollapse,
  active,
  ...props
}) {
  const location = useLocation();
  const {
    canReadSettings,
    canReadClients,
    canReadAppointment,
    canReadProperty,
    canReadStaff,
    canReadVouchers,
    canReadReceipt,
    canWriteReceipt,
    canReadExpenditure,
    canReadCashflow,
    canReadCommission,
    canReadMarketers,
  } = useAllPermissions();
  const { openModal, confirmLogout } = useLogout();
  const [openDropdown, setOpenDropdown] = useState("");

  const isActive = (key) => {
    if (active === key || props[key]) return true;
    const routes = routeGroups[key] || [];
    return routes.some((route) => location.pathname.startsWith(route));
  };

  const itemClass = (key) => (isActive(key) ? ACTIVE_CLASS : props[key]);
  const groupClass = (keys, fallback) =>
    keys.some(isActive) ? ACTIVE_CLASS : fallback;
  const tooltipProps = (label) => ({
    title: label,
    "aria-label": label,
  });
  const collapseProps = (key, label) => ({
    ...tooltipProps(label),
    open: openDropdown === key,
    onClick: () =>
      setOpenDropdown((current) => (current === key ? "" : key)),
  });
  const handleNavItemClick = () => {
    setOpenDropdown("");
    closeSidebar();
  };
  const navLabel = (label) =>
    isCollapsed ? (
      label
    ) : (
      <Tooltip content={label} placement="right">
        <span>{label}</span>
      </Tooltip>
    );

  return (
    <>
      <SessionExpiredModal open={openModal} onOk={confirmLogout} />
      <div
        onClick={closeSidebar}
        className={`fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm transition-opacity z-30 md:hidden ${
          isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <Sidebar
        aria-label="Sidebar"
        collapsed={isCollapsed}
        className={`
          dashboard-sidebar
          fixed top-0 left-0 h-screen z-50
          bg-white/90 dark:bg-gray-900/80 backdrop-blur-md shadow-xl
          border-r border-gray-200 dark:border-gray-700
          overflow-y-auto overflow-x-hidden
          transform transition-all duration-300 ease-in-out
          w-72
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${isCollapsed ? "side-bar-collapsed" : ""}
          side-bar
        `}
      >
        <div
          className={`hidden md:flex items-center border-b border-gray-200 px-3 py-3 dark:border-gray-700 ${
            isCollapsed ? "justify-center" : "justify-end"
          }`}
        >
          <button
            type="button"
            onClick={toggleCollapse}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition hover:bg-blue-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-gray-800"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <HiMenuAlt2 className="h-5 w-5" />
          </button>
        </div>

        <Sidebar.Items>
          <Sidebar.ItemGroup key={`${location.pathname}-${openDropdown}`}>
            {canReadAppointment && (
              <Sidebar.Collapse
                {...collapseProps("notifications", "Notifications")}
                icon={HiChartPie}
                label="Notifications"
                className={groupClass(
                  ["appointments", "message"],
                  props.dashboard,
                )}
              >
                <Sidebar.Item
                  as={Link}
                  to="/appointments"
                  icon={HiCalendar}
                  onClick={handleNavItemClick}
                  className={itemClass("appointments")}
                  {...tooltipProps("Appointments")}
                >
                  {navLabel("Appointments")}
                </Sidebar.Item>
                <Sidebar.Item
                  as={Link}
                  to="/messages"
                  icon={HiMail}
                  onClick={handleNavItemClick}
                  className={itemClass("message")}
                  {...tooltipProps("Messages")}
                >
                  {navLabel("Messages")}
                </Sidebar.Item>
              </Sidebar.Collapse>
            )}

            <Sidebar.Item
              as={Link}
              to="/tasks"
              className={itemClass("tasks")}
              icon={GrTask}
              onClick={handleNavItemClick}
              {...tooltipProps("Tasks")}
            >
              {navLabel("Tasks")}
            </Sidebar.Item>

            {canReadClients && (
              <Sidebar.Item
                as={Link}
                to="/clients"
                className={itemClass("clients")}
                icon={GrUserSettings}
                onClick={handleNavItemClick}
                {...tooltipProps("Clients")}
              >
                {navLabel("Clients")}
              </Sidebar.Item>
            )}

            {canReadStaff && (
              <Sidebar.Collapse
                {...collapseProps("employees", "Employees")}
                icon={MdPeople}
                label="Employees"
                className={groupClass(
                  ["staff", "appraisal", "payslip"],
                  props.users,
                )}
              >
                <Sidebar.Item
                  as={Link}
                  to="/staff"
                  icon={HiUserGroup}
                  onClick={handleNavItemClick}
                  className={itemClass("staff")}
                  {...tooltipProps("Staff")}
                >
                  {navLabel("Staff")}
                </Sidebar.Item>
                <Sidebar.Item
                  as={Link}
                  to="/appraisal"
                  icon={MdAssessment}
                  onClick={handleNavItemClick}
                  className={itemClass("appraisal")}
                  {...tooltipProps("Appraisal")}
                >
                  {navLabel("Appraisal")}
                </Sidebar.Item>
                <Sidebar.Item
                  as={Link}
                  to="/payslip"
                  icon={MdPayment}
                  onClick={handleNavItemClick}
                  className={itemClass("payslip")}
                  {...tooltipProps("Payslip")}
                >
                  {navLabel("Payslip")}
                </Sidebar.Item>
              </Sidebar.Collapse>
            )}

            {canReadExpenditure && (
              <Sidebar.Collapse
                {...collapseProps("expenditures", "Expenditures")}
                icon={TbReport}
                label="Expenditures"
                className={groupClass(
                  ["capex", "opex", "vouchers"],
                  props.expenditure,
                )}
              >
                <Sidebar.Item
                  as={Link}
                  to="/capex"
                  icon={MdOutlineTask}
                  onClick={handleNavItemClick}
                  className={itemClass("capex")}
                  {...tooltipProps("CapEx")}
                >
                  {navLabel("CapEx")}
                </Sidebar.Item>
                <Sidebar.Item
                  as={Link}
                  to="/opex"
                  icon={MdOutlineTaskAlt}
                  onClick={handleNavItemClick}
                  className={itemClass("opex")}
                  {...tooltipProps("OpEx")}
                >
                  {navLabel("OpEx")}
                </Sidebar.Item>
                {canReadVouchers && (
                  <Sidebar.Item
                    as={Link}
                    to="/vouchers"
                    icon={FaWpforms}
                    onClick={handleNavItemClick}
                    className={itemClass("vouchers")}
                    {...tooltipProps("Vouchers")}
                  >
                    {navLabel("Vouchers")}
                  </Sidebar.Item>
                )}
              </Sidebar.Collapse>
            )}

            {canReadProperty && (
              <Sidebar.Item
                as={Link}
                to="/properties"
                icon={GrProjects}
                onClick={handleNavItemClick}
                className={itemClass("projects")}
                {...tooltipProps("Properties")}
              >
                {navLabel("Properties")}
              </Sidebar.Item>
            )}

            {canReadCommission && (
              <Sidebar.Collapse
                {...collapseProps("agency", "Agency")}
                icon={MdAccountBalanceWallet}
                label="Agency"
                className={groupClass(
                  ["marketers", "commissions"],
                  props.agency,
                )}
              >
                {canReadMarketers && (
                  <Sidebar.Item
                    as={Link}
                    to="/marketers"
                    icon={MdNaturePeople}
                    onClick={handleNavItemClick}
                    className={itemClass("marketers")}
                    {...tooltipProps("Marketers")}
                  >
                    {navLabel("Marketers")}
                  </Sidebar.Item>
                )}
                <Sidebar.Item
                  as={Link}
                  to="/commissions"
                  icon={MdAttachMoney}
                  onClick={handleNavItemClick}
                  className={itemClass("commissions")}
                  {...tooltipProps("Commissions")}
                >
                  {navLabel("Commissions")}
                </Sidebar.Item>
              </Sidebar.Collapse>
            )}

            {canReadCashflow && (
              <Sidebar.Item
                as={Link}
                to="/cashflow"
                icon={TbReport}
                onClick={handleNavItemClick}
                className={itemClass("cashflow")}
                {...tooltipProps("Records")}
              >
                {navLabel("Records")}
              </Sidebar.Item>
            )}

            {canReadReceipt && (
              <Sidebar.Collapse
                {...collapseProps("receipt", "E-Receipt")}
                icon={TbReport}
                label="E-Receipt"
                className={groupClass(
                  ["newReceipt", "viewReceipt"],
                  props.receipts,
                )}
              >
                {canWriteReceipt && (
                  <Sidebar.Item
                    as={Link}
                    to="/receipts"
                    icon={MdNaturePeople}
                    onClick={handleNavItemClick}
                    className={itemClass("newReceipt")}
                    {...tooltipProps("New Receipt")}
                  >
                    {navLabel("New Receipt")}
                  </Sidebar.Item>
                )}
                <Sidebar.Item
                  as={Link}
                  to="/view-receipts"
                  icon={MdAttachMoney}
                  onClick={handleNavItemClick}
                  className={itemClass("viewReceipt")}
                  {...tooltipProps("View Receipts")}
                >
                  {navLabel("View Receipts")}
                </Sidebar.Item>
              </Sidebar.Collapse>
            )}

            {canReadSettings && (
              <Sidebar.Collapse
                {...collapseProps("settings", "Settings")}
                icon={MdSettings}
                label="Settings"
                className={groupClass(
                  ["siteSettings", "budget"],
                  props.settings,
                )}
              >
                <Sidebar.Item
                  as={Link}
                  to="/site-settings"
                  icon={BsBrowserChrome}
                  onClick={handleNavItemClick}
                  className={itemClass("siteSettings")}
                  {...tooltipProps("Site")}
                >
                  {navLabel("Site")}
                </Sidebar.Item>
                <Sidebar.Item
                  as={Link}
                  to="/budget-settings"
                  icon={GiMoneyStack}
                  onClick={handleNavItemClick}
                  className={itemClass("budget")}
                  {...tooltipProps("Budget")}
                >
                  {navLabel("Budget")}
                </Sidebar.Item>
              </Sidebar.Collapse>
            )}
          </Sidebar.ItemGroup>
        </Sidebar.Items>
      </Sidebar>
    </>
  );
}
